import * as Castle from "@castleio/castle-js";

export const ANTIBOT_TOKEN_HEADER = "X-Castle-Request-Token";
export const ANTIBOT_TOKEN_FIELD = "castle_request_token";

const TIMEOUT_IN_MILLISECONDS = 2000;

type CastleInstance = ReturnType<typeof Castle.configure>;

type HeaderValue = string | number | boolean;

export type AntibotRequestConfig = {
  headers?: HeadersInit | Record<string, HeaderValue>;
};

let castleInstance: CastleInstance | undefined;

function publishableKey(): string | undefined {
  return window.antibot_castle_publishable_key || undefined;
}

function castle(): CastleInstance | undefined {
  const pk = publishableKey();

  if (!pk) {
    return undefined;
  }

  castleInstance ??= Castle.configure({ pk, timeout: TIMEOUT_IN_MILLISECONDS });

  return castleInstance;
}

async function withTimeout(
  requestToken: PromiseLike<string>
): Promise<string | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), TIMEOUT_IN_MILLISECONDS);
  });

  try {
    return await Promise.race([requestToken, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

export function initAntibot(): void {
  try {
    castle();
  } catch {
    castleInstance = undefined;
  }
}

export async function fetchToken(): Promise<string | undefined> {
  try {
    const instance = castle();

    if (!instance) {
      return undefined;
    }

    return (await withTimeout(instance.createRequestToken())) || undefined;
  } catch {
    return undefined;
  }
}

function existingTokenInput(form: HTMLFormElement): HTMLInputElement | null {
  return form.querySelector<HTMLInputElement>(
    `input[name="${ANTIBOT_TOKEN_FIELD}"]`
  );
}

function tokenInput(form: HTMLFormElement): HTMLInputElement {
  const existing = existingTokenInput(form);

  if (existing) {
    return existing;
  }

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = ANTIBOT_TOKEN_FIELD;
  form.appendChild(input);

  return input;
}

export async function injectIntoForm(form: HTMLFormElement): Promise<void> {
  const token = await fetchToken();

  if (!token) {
    existingTokenInput(form)?.remove();
    return;
  }

  tokenInput(form).value = token;
}

function isTokenHeader(name: string): boolean {
  return name.toLowerCase() === ANTIBOT_TOKEN_HEADER.toLowerCase();
}

function withTokenHeader(
  headers: AntibotRequestConfig["headers"],
  token: string
): AntibotRequestConfig["headers"] {
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    const copy = new Headers(headers);
    copy.set(ANTIBOT_TOKEN_HEADER, token);
    return copy;
  }

  if (Array.isArray(headers)) {
    const others = headers.filter(([name]) => !isTokenHeader(name));
    return [...others, [ANTIBOT_TOKEN_HEADER, token]];
  }

  const others = Object.fromEntries(
    Object.entries(headers ?? {}).filter(([name]) => !isTokenHeader(name))
  );

  return { ...others, [ANTIBOT_TOKEN_HEADER]: token };
}

export async function applyToRequest<T extends AntibotRequestConfig>(
  config: T
): Promise<T> {
  const token = await fetchToken();

  if (!token) {
    return config;
  }

  return { ...config, headers: withTokenHeader(config.headers, token) };
}
